/**
 * Optional-skip surface plan for new Explore Unknown runs (React client copy).
 *
 * Canonical: `modules/exploreUnknownBonusResolver.js` (`resolveOptionalSkipPlanForNewRun`).
 * Keep in sync; `ExploreUnknown_OptionalSkip_NewRun_Regression.js` enforces parity.
 */

/**
 * Optional-skip defs unlocked at `level`, low tier → high tier.
 * @param {number} level
 * @param {Record<string, object>} levelDefinitions
 * @returns {object[]}
 */
export function listUnlockedOptionalSkipDefs(level, levelDefinitions) {
  const lv = Number(level);
  if (!Number.isInteger(lv) || lv < 1) {
    throw new Error(`listUnlockedOptionalSkipDefs: invalid level (${String(level)})`);
  }
  return Object.values(levelDefinitions || {})
    .filter((d) => d && d.type === 'optionalSkip' && lv >= d.level)
    .sort((a, b) => a.level - b.level);
}

/**
 * Enforce prefix invariant: enabled skip tiers are always 0..k with no gaps.
 * @param {Record<string, boolean>} skippingBonuses
 * @param {number} level
 * @param {Record<string, object>} levelDefinitions
 * @returns {Record<string, boolean>}
 */
export function normalizeSkippingBonuses(skippingBonuses, level, levelDefinitions) {
  const defs = listUnlockedOptionalSkipDefs(level, levelDefinitions);
  const out = { ...(skippingBonuses || {}) };
  let topEnabledIdx = -1;
  for (let i = defs.length - 1; i >= 0; i -= 1) {
    if (out[defs[i].bonusId] !== false) {
      topEnabledIdx = i;
      break;
    }
  }
  for (let i = 0; i < defs.length; i += 1) {
    out[defs[i].bonusId] = i <= topEnabledIdx;
  }
  return out;
}

/**
 * Apply optional-skip toggle with tier cascade (client copy — keep in sync with server resolver).
 * @param {Record<string, boolean>} skippingBonuses
 * @param {string} bonusId
 * @param {boolean} enabled
 * @param {number} level
 * @param {Record<string, object>} levelDefinitions
 * @returns {Record<string, boolean>}
 */
export function applySkippingBonusToggleCascade(skippingBonuses, bonusId, enabled, level, levelDefinitions) {
  const defs = listUnlockedOptionalSkipDefs(level, levelDefinitions);
  const idx = defs.findIndex((d) => d.bonusId === bonusId);
  if (idx < 0) {
    throw new Error(`applySkippingBonusToggleCascade: unknown bonusId (${String(bonusId)})`);
  }
  const out = { ...(skippingBonuses || {}) };
  if (enabled === false) {
    out[bonusId] = false;
    for (let i = idx + 1; i < defs.length; i += 1) {
      out[defs[i].bonusId] = false;
    }
  } else {
    out[bonusId] = true;
    for (let i = 0; i < idx; i += 1) {
      out[defs[i].bonusId] = true;
    }
  }
  return normalizeSkippingBonuses(out, level, levelDefinitions);
}

/**
 * @param {ReadonlyArray<object>} activeEffects
 * @returns {{ initialSurface: string, skippedSurfaces: string[] }}
 */
export function resolveOptionalSkipPlanFromEffects(activeEffects) {
  if (!Array.isArray(activeEffects)) {
    throw new Error('resolveOptionalSkipPlanFromEffects: activeEffects must be an array');
  }
  const skips = activeEffects
    .filter(
      (e) =>
        e &&
        e.kind === 'optionalSkipSurface' &&
        typeof e.surface === 'string' &&
        typeof e.startSurface === 'string'
    )
    .sort((a, b) => (Number(a.tier) || 0) - (Number(b.tier) || 0));
  if (skips.length === 0) {
    return { initialSurface: 'arrival', skippedSurfaces: [] };
  }
  return {
    initialSurface: skips[skips.length - 1].startSurface,
    skippedSurfaces: skips.map((s) => s.surface),
  };
}

/**
 * @param {number} level
 * @param {Record<string, object>} levelDefinitions
 * @param {Record<string, boolean>} skippingBonuses bonusId -> enabled (default true when unlocked)
 * @returns {{ initialSurface: string, skippedSurfaces: string[] }}
 */
export function resolveOptionalSkipPlanForPlayer(level, levelDefinitions, skippingBonuses = {}) {
  const lv = Number(level);
  if (!Number.isInteger(lv) || lv < 1) {
    throw new Error(`resolveOptionalSkipPlanForPlayer: invalid level (${String(level)})`);
  }
  const activeEffects = [];
  for (const def of Object.values(levelDefinitions || {})) {
    if (!def || def.type !== 'optionalSkip' || lv < def.level) continue;
    if (skippingBonuses[def.bonusId] === false) continue;
    if (def.effects?.kind === 'optionalSkipSurface') {
      activeEffects.push(def.effects);
    }
  }
  return resolveOptionalSkipPlanFromEffects(activeEffects);
}
